import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';

const STATUS_COLORS = {
  pending:  { bg: 'rgba(251,191,36,0.1)',  border: 'rgba(251,191,36,0.3)',  text: '#fbbf24', label: '⏳ Pending' },
  approved: { bg: 'rgba(34,197,94,0.1)',   border: 'rgba(34,197,94,0.3)',   text: '#4ade80', label: '✅ Approved' },
  rejected: { bg: 'rgba(248,68,68,0.1)',   border: 'rgba(248,68,68,0.3)',   text: '#f87171', label: '❌ Rejected' },
};

const VendorRequests = () => {
  const [vendors, setVendors]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState('pending'); // 'pending' | 'all'
  const [expanded, setExpanded]   = useState(null);      // vendor _id
  const [toast, setToast]         = useState(null);

  // Reject modal state
  const [rejectModal, setRejectModal] = useState(null); // { id, name }
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [approveResult, setApproveResult] = useState(null); // Show temp password after approval

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/vendor-applications');
      if (res.data.success) setVendors(res.data.data);
    } catch (err) {
      showToast('error', 'Failed to load vendor applications.');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 4000);
  };

  const handleApprove = async (id) => {
    setActionLoading(true);
    try {
      const res = await api.put(`/admin/vendor-applications/${id}/approve`);
      if (res.data.success) {
        setApproveResult(res.data.data); // { loginEmail, tempPassword, vendorId, userId }
        setVendors(prev => prev.filter(v => v._id !== id)); // Remove from list (like CentreRequests)
        setExpanded(null);
        showToast('success', '✅ Vendor approved! Login credentials sent by email.');
      }
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Failed to approve vendor.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectModal) return;
    setActionLoading(true);
    try {
      const res = await api.put(`/admin/vendor-applications/${rejectModal.id}/reject`, { reason: rejectReason });
      if (res.data.success) {
        setVendors(prev => prev.map(v => v._id === rejectModal.id ? { ...v, status: 'rejected', adminNote: rejectReason } : v));
        setRejectModal(null);
        setRejectReason('');
        setExpanded(null);
        showToast('success', 'Application rejected.');
      }
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Failed to reject vendor.');
    } finally {
      setActionLoading(false);
    }
  };

  const displayed = vendors.filter(v =>
    tab === 'pending'
      ? v.status === 'pending'
      : true
  );

  const pendingCount  = vendors.filter(v => v.status === 'pending').length;
  const approvedCount = vendors.filter(v => v.status === 'approved').length;
  const rejectedCount = vendors.filter(v => v.status === 'rejected').length;

  // ── Shared styles ──────────────────────────────────────────────────────────
  const card = {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(212,175,55,0.1)',
    borderRadius: '0.75rem',
    overflow: 'hidden',
    marginBottom: '0.85rem',
    transition: 'border-color 0.2s',
  };

  // ── Document Viewer Modal ──────────────────────────────────────
  const [docModal, setDocModal] = useState(null); // { label, url }

  // Detect PDFs: check .pdf extension OR /raw/upload/ path (new Cloudinary uploads)
  const isPdf = (url) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.includes('.pdf') || lower.includes('/raw/upload/');
  };


  const DocLink = ({ label, url }) => {
    if (!url) return (
      <span style={{ color: '#333', fontSize: '0.78rem' }}>{label}: <em style={{ color: '#444' }}>Not uploaded</em></span>
    );
    const handleClick = (e) => {
      e.stopPropagation();
      setDocModal({ label, url });
    };
    return (
      <button
        onClick={handleClick}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
          padding: '0.35rem 0.75rem',
          background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)',
          borderRadius: '0.35rem', color: '#E8B84B', fontSize: '0.78rem',
          fontWeight: 600, cursor: 'pointer',
        }}
      >
        {isPdf(url) ? '📄' : '🖼️'} {label}
      </button>
    );
  };

  // Document Viewer Modal JSX (rendered at top-level of component return)
  const DocViewerModal = docModal && (
    <div
      onClick={() => setDocModal(null)}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(8px)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '1.5rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxWidth: '900px', background: '#111', borderRadius: '1rem', overflow: 'hidden', border: '1px solid rgba(212,175,55,0.2)' }}
      >
        {/* Modal Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.85rem 1.25rem', borderBottom: '1px solid #1e1e1e' }}>
          <span style={{ color: '#E8B84B', fontWeight: 700, fontSize: '0.875rem' }}>
            {isPdf(docModal.url) ? '📄' : '🖼️'} {docModal.label}
          </span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <a
              href={docModal.url}
              target="_blank"
              rel="noreferrer"
              style={{ padding: '0.35rem 0.85rem', background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.25)', borderRadius: '0.4rem', color: '#E8B84B', fontSize: '0.75rem', fontWeight: 600, textDecoration: 'none' }}
            >
              ↗ Open in Tab
            </a>
            <button
              onClick={() => setDocModal(null)}
              style={{ padding: '0.35rem 0.85rem', background: 'rgba(248,68,68,0.1)', border: '1px solid rgba(248,68,68,0.25)', borderRadius: '0.4rem', color: '#f87171', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
            >
              ✕ Close
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ background: '#0a0a0a', height: '75vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {isPdf(docModal.url) ? (
            <object
              data={docModal.url}
              type="application/pdf"
              style={{ width: '100%', height: '100%', border: 'none', backgroundColor: '#fff' }}
            >
              <div style={{ textAlign: 'center', padding: '2rem', color: '#fff' }}>
                <p>Your browser does not support inline PDF viewing.</p>
                <a href={docModal.url} target="_blank" rel="noreferrer" style={{ color: '#E8B84B', textDecoration: 'underline' }}>Click here to download the PDF</a>
              </div>
            </object>
          ) : (
            <img
              src={docModal.url}
              alt={docModal.label}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '0.5rem' }}
            />
          )}
        </div>
      </div>
    </div>
  );


  const Field = ({ label, value }) => (
    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', marginBottom: '0.4rem' }}>
      <span style={{ color: '#666', fontSize: '0.75rem', minWidth: '160px', flexShrink: 0 }}>{label}</span>
      <span style={{ color: '#ccc', fontSize: '0.82rem', wordBreak: 'break-word' }}>{value || <em style={{ color: '#444' }}>—</em>}</span>
    </div>
  );

  return (
    <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto' }}>

      {/* Document Viewer Modal */}
      {DocViewerModal}

      {/* Page Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: 900, color: '#fff', marginBottom: '0.4rem' }}>Vendor Applications</h1>
        <p style={{ color: '#555', fontSize: '0.85rem' }}>Review, approve, or reject vendor onboarding applications.</p>
      </div>

      {/* Stats Row */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.75rem', flexWrap: 'wrap' }}>
        {[
          { label: 'Pending Review', count: pendingCount,  color: '#fbbf24' },
          { label: 'Approved',       count: approvedCount, color: '#4ade80' },
          { label: 'Rejected',       count: rejectedCount, color: '#f87171' },
        ].map(s => (
          <div key={s.label} style={{ flex: '1 1 140px', padding: '1rem 1.25rem', background: 'rgba(255,255,255,0.03)', border: '1px solid #1e1e1e', borderRadius: '0.75rem' }}>
            <div style={{ fontSize: '1.75rem', fontWeight: 900, color: s.color }}>{s.count}</div>
            <div style={{ color: '#555', fontSize: '0.78rem', marginTop: '0.2rem' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #1e1e1e', paddingBottom: '0.75rem' }}>
        {[{ key: 'pending', label: `Pending (${pendingCount})` }, { key: 'all', label: `All Applications (${vendors.length})` }].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '0.45rem 1.1rem', borderRadius: '0.4rem', border: 'none', cursor: 'pointer',
              background: tab === t.key ? 'rgba(212,175,55,0.15)' : 'transparent',
              color: tab === t.key ? '#E8B84B' : '#555',
              fontWeight: tab === t.key ? 700 : 400,
              fontSize: '0.85rem', transition: 'all 0.2s',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Approval Result: Show temp password — same pattern as CentreRequests.js */}
      {approveResult && (
        <div style={{ padding: '1.25rem', marginBottom: '1.25rem', borderRadius: '0.75rem', background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.25)' }}>
          <h3 style={{ color: '#4ade80', fontWeight: 700, marginBottom: '0.75rem', fontSize: '1rem' }}>✅ Vendor Account Created!</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div>
              <div style={{ color: '#555', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.3rem' }}>Login Email</div>
              <div style={{ fontFamily: 'monospace', background: 'rgba(34,197,94,0.08)', padding: '0.4rem 0.6rem', borderRadius: '0.4rem', color: '#4ade80', fontSize: '0.85rem', wordBreak: 'break-all' }}>{approveResult.loginEmail}</div>
            </div>
            <div>
              <div style={{ color: '#555', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.3rem' }}>Temporary Password <span style={{ color: '#f87171' }}>(share securely!)</span></div>
              <div style={{ fontFamily: 'monospace', background: 'rgba(212,175,55,0.1)', padding: '0.4rem 0.6rem', borderRadius: '0.4rem', color: '#E8B84B', fontWeight: 900, fontSize: '1.1rem', letterSpacing: '0.1em' }}>{approveResult.tempPassword}</div>
            </div>
          </div>
          <p style={{ color: '#555', fontSize: '0.75rem', margin: '0 0 0.75rem' }}>
            ⚠️ The vendor will receive an email with these credentials. Share this password securely as a backup in case the email is not received.
          </p>
          <button
            onClick={() => setApproveResult(null)}
            style={{ padding: '0.4rem 1rem', background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '0.4rem', color: '#4ade80', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ background: toast.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(248,68,68,0.1)', border: `1px solid ${toast.type === 'success' ? 'rgba(34,197,94,0.3)' : 'rgba(248,68,68,0.3)'}`, borderRadius: '0.5rem', padding: '0.85rem 1rem', color: toast.type === 'success' ? '#4ade80' : '#f87171', marginBottom: '1rem', fontSize: '0.85rem' }}>
          {toast.text}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '4rem 0' }}>
          <div style={{ width: '32px', height: '32px', border: '3px solid #1e1e1e', borderTop: '3px solid #E8B84B', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }} />
          <p style={{ color: '#444', fontSize: '0.85rem' }}>Loading vendor applications…</p>
        </div>
      ) : displayed.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem 0' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🏪</div>
          <p style={{ color: '#444' }}>{tab === 'pending' ? 'No pending vendor applications.' : 'No vendor applications yet.'}</p>
        </div>
      ) : (
        displayed.map(v => {
          const status    = v.status || 'pending';
          const sc        = STATUS_COLORS[status] || STATUS_COLORS.pending;
          const isOpen    = expanded === v._id;
          const hasFood   = (v.businessCategories || []).some(c => ['Sweets/SevaOffering', 'Oils & Ghee'].includes(c));

          return (
            <div key={v._id} style={{ ...card, borderColor: isOpen ? 'rgba(212,175,55,0.25)' : 'rgba(212,175,55,0.08)' }}>

              {/* Card Header — always visible */}
              <div
                onClick={() => setExpanded(isOpen ? null : v._id)}
                style={{ padding: '1.1rem 1.4rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '1rem', userSelect: 'none' }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
                    <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>
                      {v.shopName || v.legalEntityName || 'Unnamed Business'}
                    </span>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: '999px', background: sc.bg, border: `1px solid ${sc.border}`, color: sc.text }}>
                      {sc.label}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
                    {v.contactPerson && <span style={{ color: '#666', fontSize: '0.78rem' }}>👤 {v.contactPerson}</span>}
                    {v.email         && <span style={{ color: '#666', fontSize: '0.78rem' }}>✉️ {v.email}</span>}
                    {v.phone         && <span style={{ color: '#666', fontSize: '0.78rem' }}>📞 {v.phone}</span>}
                    {v.createdAt     && <span style={{ color: '#444', fontSize: '0.75rem' }}>🗓 {new Date(v.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                  </div>
                  {(v.businessCategories || []).length > 0 && (
                    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginTop: '0.4rem' }}>
                      {v.businessCategories.map(c => (
                        <span key={c} style={{ fontSize: '0.68rem', padding: '0.15rem 0.5rem', background: 'rgba(212,175,55,0.07)', border: '1px solid rgba(212,175,55,0.15)', borderRadius: '999px', color: '#E8B84B' }}>{c}</span>
                      ))}
                    </div>
                  )}
                </div>
                <span style={{ color: '#555', fontSize: '0.85rem', flexShrink: 0 }}>{isOpen ? '▲' : '▼'}</span>
              </div>

              {/* Expanded Details */}
              {isOpen && (
                <div style={{ borderTop: '1px solid #1a1a1a', padding: '1.4rem' }}>

                  {/* Business Info */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ color: '#E8B84B', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.85rem' }}>Business Information</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.25rem 2rem' }}>
                      <Field label="Legal Entity Name"  value={v.legalEntityName} />
                      <Field label="Brand / Shop Name"  value={v.shopName} />
                      <Field label="Business Type"      value={v.businessType} />
                      <Field label="Contact Person"     value={v.contactPerson} />
                      <Field label="Email"              value={v.email} />
                      <Field label="Phone"              value={v.phone} />
                    </div>
                    {v.businessDescription && (
                      <div style={{ marginTop: '0.6rem', padding: '0.75rem', background: 'rgba(255,255,255,0.02)', borderRadius: '0.4rem' }}>
                        <p style={{ color: '#777', fontSize: '0.78rem', margin: 0 }}>{v.businessDescription}</p>
                      </div>
                    )}
                  </div>

                  {/* KYC Numbers */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ color: '#E8B84B', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.85rem' }}>Tax & Registration Numbers</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.25rem 2rem' }}>
                      <Field label="GSTIN"         value={v.gstin} />
                      <Field label="PAN Number"    value={v.panNumber} />
                      {hasFood && <Field label="FSSAI License" value={v.fssaiLicense} />}
                    </div>
                  </div>

                  {/* Address */}
                  {(v.registeredAddress || v.warehouseAddress) && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h3 style={{ color: '#E8B84B', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.85rem' }}>Address Details</h3>
                      <Field label="Registered Address" value={v.registeredAddress} />
                      {v.warehouseAddress && <Field label="Warehouse Address"  value={v.warehouseAddress} />}
                    </div>
                  )}

                  {/* Documents */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ color: '#E8B84B', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.85rem' }}>Documents</h3>
                    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                      <DocLink label="GSTIN Certificate" url={v.documents?.gstinCert} />
                      <DocLink label="PAN Card"          url={v.documents?.panCard} />
                      {hasFood && <DocLink label="FSSAI Certificate" url={v.documents?.fssaiCert} />}
                      <DocLink label="Cancelled Cheque"  url={v.bank?.cancelledCheck} />
                    </div>
                  </div>

                  {/* Bank Details */}
                  {v.bank?.bankName && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h3 style={{ color: '#E8B84B', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.85rem' }}>Bank Details</h3>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.25rem 2rem' }}>
                        <Field label="Bank Name"       value={v.bank.bankName} />
                        <Field label="Account Holder"  value={v.bank.accountHolder} />
                        <Field label="Account Number"  value={v.bank.accountNumber ? '••••' + v.bank.accountNumber.slice(-4) : '—'} />
                        <Field label="IFSC Code"       value={v.bank.ifscCode} />
                      </div>
                    </div>
                  )}

                  {/* Rejection Reason */}
                  {status === 'rejected' && v.adminNote && (
                    <div style={{ marginBottom: '1.5rem', padding: '0.85rem 1rem', background: 'rgba(248,68,68,0.06)', border: '1px solid rgba(248,68,68,0.15)', borderRadius: '0.5rem' }}>
                      <p style={{ color: '#f87171', fontSize: '0.8rem', margin: 0 }}>
                        <strong>Rejection Reason:</strong> {v.adminNote}
                      </p>
                    </div>
                  )}

                  {/* Action Buttons — only for pending */}
                  {status === 'pending' && (
                    <div style={{ display: 'flex', gap: '0.75rem', paddingTop: '0.5rem', borderTop: '1px solid #1a1a1a', marginTop: '0.5rem' }}>
                      <button
                        onClick={() => handleApprove(v._id)}
                        disabled={actionLoading}
                        style={{
                          flex: 2, padding: '0.75rem', borderRadius: '0.5rem',
                          background: actionLoading ? '#333' : 'linear-gradient(90deg, #22c55e, #16a34a)',
                          border: 'none', color: '#fff', fontWeight: 700, cursor: actionLoading ? 'not-allowed' : 'pointer',
                          fontSize: '0.875rem',
                        }}
                      >
                        {actionLoading ? '⏳ Processing…' : '✅ Approve & Send Credentials'}
                      </button>
                      <button
                        onClick={() => { setRejectModal({ id: v._id, name: v.shopName || v.legalEntityName }); setRejectReason(''); }}
                        disabled={actionLoading}
                        style={{
                          flex: 1, padding: '0.75rem', borderRadius: '0.5rem',
                          background: 'transparent', border: '1px solid rgba(248,68,68,0.4)',
                          color: '#f87171', fontWeight: 700, cursor: 'pointer', fontSize: '0.875rem',
                        }}
                      >
                        ❌ Reject
                      </button>
                    </div>
                  )}

                </div>
              )}
            </div>
          );
        })
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
        }}>
          <div style={{
            background: '#111', border: '1px solid rgba(248,68,68,0.25)', borderRadius: '1rem',
            padding: '2rem', width: '100%', maxWidth: '480px',
          }}>
            <h3 style={{ color: '#f87171', fontWeight: 700, marginBottom: '0.5rem', fontSize: '1.05rem' }}>Reject Application</h3>
            <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: '1.25rem' }}>
              Rejecting <strong style={{ color: '#ccc' }}>{rejectModal.name}</strong>. Optionally provide a reason so the vendor can re-apply with correct information.
            </p>
            <label style={{ display: 'block', color: '#888', fontSize: '0.8rem', marginBottom: '0.4rem' }}>Rejection Reason <span style={{ color: '#444' }}>(optional)</span></label>
            <textarea
              rows="3"
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="e.g. GSTIN document is unclear, please re-upload a legible copy."
              style={{ width: '100%', padding: '0.75rem', background: '#1a1a1a', border: '1px solid #333', borderRadius: '0.5rem', color: '#fff', boxSizing: 'border-box', resize: 'vertical', fontSize: '0.875rem' }}
            />
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
              <button
                onClick={() => setRejectModal(null)}
                style={{ flex: 1, padding: '0.75rem', background: 'transparent', border: '1px solid #333', borderRadius: '0.5rem', color: '#666', cursor: 'pointer', fontWeight: 600 }}
              >
                Cancel
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={actionLoading}
                style={{ flex: 2, padding: '0.75rem', background: actionLoading ? '#333' : 'rgba(248,68,68,0.15)', border: '1px solid rgba(248,68,68,0.4)', borderRadius: '0.5rem', color: '#f87171', fontWeight: 700, cursor: actionLoading ? 'not-allowed' : 'pointer' }}
              >
                {actionLoading ? '⏳ Processing…' : '❌ Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spinner keyframes */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default VendorRequests;
